export class PerfectShotImageManagerPane extends HTMLElement {
    connectedCallback() {
        this.addEventListener('click', this.handleClick.bind(this));
        document.body.addEventListener('perfect-shot-case-reviewed', this.handleCaseReviewed.bind(this));

        document.body.addEventListener('perfect-shot-image-selected', event => {
            const dicomId = parseInt(event.detail?.dicom_id);
            if (this.querySelector(`.ps-row[data-dicom_id="${dicomId}"]`)) {
                this.showImage(dicomId);
            }
        });

        // viewer → panel: initial load, sidebar thumbnail, prev/next arrows all end in this
        // event (PatientSwissKnife.js:5135).  It is dispatched on .vetology_canvas without
        // bubbles, so listen in the capture phase — works before the canvas exists too.
        document.addEventListener('vetology-image-loaded', event => {
            const imageId = parseInt(event.detail?.image_id);
            if (!isNaN(imageId)) {
                this.markViewing(imageId);
            }
        }, true);
    }

    get caseId() { return parseInt(this.dataset.case_id); }

    /** this case's tab button, in whichever menu row it lives */
    get tabButton() {
        return document.querySelector(`ul.top-report-menu[data-case_id="${this.caseId}"] .ps-nav-top .nav-button`);
    }

    handleClick(event) {
        if (event.target.closest('.ps-review-btn')) {
            document.querySelector('perfect-shot-review-dialog')?.open(this.caseId, { docked: true });   // §8.3
            return;
        }
        const chevron = event.target.closest('.ps-row__chevron');
        if (chevron) {
            const row = chevron.closest('.ps-row');
            row.classList.toggle('expanded');
            chevron.setAttribute('aria-expanded', row.classList.contains('expanded'));
            return;
        }
        if (event.target.closest('.ps-row__detail')) {
            return;     // reading/selecting the guidance is not a "show this image" click
        }
        
        const row = event.target.closest('.ps-row');
        if (row) {
            this.showImage(parseInt(row.dataset.dicom_id));
        }
    }

    /** row click → the viewer shows that image; the stripe follows via markViewing() */
    showImage(dicomId) {
        // Verified 2026-09-11: the series tile is built at PatientSwissKnife.js:1735-1745 with
        // data-image_id = the series' current dicom id (CaseManagerRenderHelper.php:212 sets
        // image_id = dicoms.id — same key space as our dicom_id).  The click handler is
        // delegated on the INNER `.series_activator` (PatientSwissKnife.js:2144), not the tile,
        // so that is what we click.  One image per series is the PS case today; a multi-image
        // series would also need _setStackIndex() — 26980's problem.
        document.querySelector(`.series_loader_iv[data-image_id="${dicomId}"] .series_activator`)?.click();
        this.markViewing(dicomId);
    }

    markViewing(dicomId) {
        this.querySelectorAll('.ps-row.viewing').forEach(r => r.classList.remove('viewing'));
        this.querySelector(`.ps-row[data-dicom_id="${dicomId}"]`)?.classList.add('viewing');
        this.scrollToViewing();
    }

    /** one listener per pane; each answers only for its own case (main + related all coexist) */
    handleCaseReviewed(event) {
        if (parseInt(event.detail?.case_id) !== this.caseId) {
            return;
        }
        this.classList.add('reviewed');

        // mirrors the blade: reviewed studies start with every row collapsed
        this.querySelectorAll('.ps-row.expanded').forEach(row => {
            row.classList.remove('expanded');
            row.querySelector('.ps-row__chevron')?.setAttribute('aria-expanded', 'false');
        });

        // mirrors perfect_shot_pane.blade.php's is_reviewed footer branch
        const footer = this.querySelector('.ps-pane__footer');
        const review = event.detail.review;
        const justified = (event.detail.images ?? []).length;
        if (footer) {
            footer.innerHTML = '<span class="ps-pane__reviewed"><vetology-svg data-svg="check_circle"></vetology-svg><span class="ps-pane__reviewed-label">Reviewed</span></span>'
                + (review ? `<div class="ps-pane__review-details">${this.esc(review.reviewed_at)} · ${justified} ${justified === 1 ? 'image' : 'images'} justified</div>` : '');
        }

        if (review && !this.querySelector('.ps-pill--reviewed')) {
            const pill = document.createElement('span');
            pill.className = 'ps-pill ps-pill--reviewed';
            pill.textContent = `Reviewed by ${review.reviewed_by} · ${review.reviewed_at}`;
            this.querySelector('.ps-pane__status')?.appendChild(pill);
        }

        // mirrors perfect_shot_tab.blade.php's is_reviewed branch — tab turns green, badge → ✓ (26981 §2.2)
        this.tabButton?.classList.remove('warning', 'fail');
        this.tabButton?.classList.add('pass');
        const badge = this.tabButton?.querySelector('.ps-badge');
        if (badge) {
            badge.className = 'ps-badge ps-badge--check';
            badge.innerHTML = '<vetology-svg data-svg="check" width="12" height="12"></vetology-svg>';
        }

        // §7.0 — fill each reviewed row's fold from the responses the dialog just posted.
        // Mirrors the blade's `image.review` branch; the list page ignores event.detail.images.
        (event.detail.images ?? []).forEach(({ dicom_id, reason_label, notes }) => {
            const detail = this.querySelector(`.ps-row[data-dicom_id="${dicom_id}"] .ps-row__detail`);
            if (!detail) {
                return;
            }
            detail.innerHTML = `<dl class="ps-review"><dt>Reason</dt><dd>${this.esc(reason_label)}</dd>`
                + (notes ? `<dt>Notes</dt><dd>${this.esc(notes)}</dd>` : '') + '</dl>';
        });
    }

    esc(text) {
        return String(text ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    scrollToViewing() {
        if (this.offsetParent === null) {
            return;     // pane hidden — ReportingWorkarea calls this again when the tab opens
        }
        this.querySelector('.ps-row.viewing')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
}

customElements.define('perfect-shot-image-manager-pane', PerfectShotImageManagerPane);
